import { Injectable } from '@angular/core';
import { HttpClient, HttpHandler, HttpParams, HttpHeaders, HttpEvent, HttpRequest } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { concatMap, finalize, shareReplay } from 'rxjs/operators';


/**
 * это сервис, расширяющий HttpClient ангуляра с возможностями, такими как дедупликация запросов
 * и организации очереди запросов.
 *
 * что дает этот сервис
 * - два компонента, делающих один и тот же GET/POST к одному и тому же URL с теми же params/body, получат один реальный запрос и разделят результат.
 * - порядок параметров и представление HttpParams не мешают дедупликации.
 * - повтор запроса после refresh (с X-Retry) не ломают ключ, т.к. заголовки не учитываются.
 * - если где-то дедупликация не нужна — можно доабвить в options.headers заголовок X-No-Dedup: '1'.
 */
@Injectable()
export class QueueHttpService extends HttpClient {
	private requestQueue$ = new Subject<Observable<unknown>>();
	private inflight = new Map<string, Observable<unknown>>();

	constructor(handler: HttpHandler) {
		super(handler);
		this.requestQueue$.pipe(concatMap((obs) => obs)).subscribe();
	}

	private isNoDedup(options?: any): boolean {
		const headers: HttpHeaders | undefined = options?.headers;
		return headers?.get('X-No-Dedup') === '1';
	}

	private toPlainParams(params: HttpParams | Record<string, any> | undefined): Record<string, string[]> {
		if (!params) return {};
		if (params instanceof HttpParams) {
			const out: Record<string, string[]> = {};
			for (const key of params.keys()) {
				out[key] = params.getAll(key) || [];
			}
			return out;
		}
		// обычный объект
		const out: Record<string, string[]> = {};
		Object.keys(params).forEach((k) => {
			const v = params[k];
			if (v == null) return;
			if (Array.isArray(v)) out[k] = v.map(String);
			else out[k] = [String(v)];
		});
		return out;
	}

	// TODO: не уверен... надо потестить...
	private stableStringify(obj: any): string {
		if (obj == null) return '';
		if (typeof FormData !== 'undefined' && obj instanceof FormData) {
			// не сериализуем FormData (часто уникальные boundary/порядок) — считаем “одинаковым” телом
			return 'FORMDATA';
		}
		if (obj instanceof Blob) return 'BLOB';
		if (typeof obj !== 'object') return JSON.stringify(obj);

		const allKeys: string[] = [];
		JSON.stringify(obj, (key, value) => { allKeys.push(key); return value; });
		allKeys.sort();
		return JSON.stringify(obj, allKeys);
	}

	private normalizeUrl(url: string): { base: string; query: Record<string, string[]> } {
		try {
			const u = new URL(url, window.location.origin);
			const query: Record<string, string[]> = {};
			for (const [k, v] of u.searchParams.entries()) {
				if (!query[k]) query[k] = [];
				query[k].push(v);
			}
			return { base: `${u.origin}${u.pathname}`, query };
		} catch {
			// fallback если url относительный без origin
			const [base, qs] = url.split('?', 2);
			const query: Record<string, string[]> = {};
			if (qs) {
				for (const pair of qs.split('&')) {
					if (!pair) continue;
					const [k, v] = pair.split('=', 2);
					const key = decodeURIComponent(k || '');
					const val = decodeURIComponent(v || '');
					if (!query[key]) query[key] = [];
					query[key].push(val);
				}
			}
			return { base, query };
		}
	}

	private buildKey(method: string, url: string, options?: any, body?: any): string {
		// 1 - URL -> base + отсортированный query
		const { base, query } = this.normalizeUrl(url);
		// 2 - options.params -> plain + объединение с query (query в URL + options.params)
		const paramsFromOptions = this.toPlainParams(options?.params);
		// объединение
		const mergedParams: Record<string, string[]> = {};
		const addAll = (src: Record<string, string[]>) => {
			Object.keys(src).forEach((k) => {
				if (!mergedParams[k]) mergedParams[k] = [];
				mergedParams[k].push(...src[k]);
			});
		};
		addAll(query);
		addAll(paramsFromOptions);
		// сортировка ключей и значений
		const paramsKeys = Object.keys(mergedParams).sort();
		const normalizedParams = paramsKeys.map((k) => `${k}=${mergedParams[k].slice().sort().join(',')}`).join('&');

		// 3 - body сериализация
		const bodyStr = this.stableStringify(body);

		// 4 - игнор headers полностью (Authorization/X-Retry не влияют) TODO: надо тестить
		return `${method.toUpperCase()}::${base}?${normalizedParams}::${bodyStr}`;
	}

	private enqueueWithKey<T>(key: string, factory: () => Observable<T>, noDedup: boolean): Observable<T> {
		if (!noDedup) {
			const existing = this.inflight.get(key) as Observable<T> | undefined;
			if (existing) return existing;
		}

		const shared$ = factory().pipe(
			shareReplay({ bufferSize: 1, refCount: true }),
			finalize(() => {
				// перестает учитывать только если не отключена дедупликация
				if (!noDedup) this.inflight.delete(key);
			}),
		);

		if (!noDedup) this.inflight.set(key, shared$);
		this.requestQueue$.next(shared$);
		return shared$;
	}

	override get<T>(url: string, options?: any): Observable<T> {
		const key = this.buildKey('GET', url, options);
		return this.enqueueWithKey<T>(
			key,
			() => super.get(url, options) as unknown as Observable<T>,
			this.isNoDedup(options),
		) as any;
	}

	override post<T>(url: string, body: any | null, options?: any): Observable<T> {
		const key = this.buildKey('POST', url, options, body);
		return this.enqueueWithKey<T>(
			key,
			() => super.post(url, body, options) as unknown as Observable<T>,
			this.isNoDedup(options),
		) as any;
	}

	override put<T>(url: string, body: any | null, options?: any): Observable<T> {
		const key = this.buildKey('PUT', url, options, body);
		return this.enqueueWithKey<T>(
			key,
			() => super.put(url, body, options) as unknown as Observable<T>,
			this.isNoDedup(options),
		) as any;
	}

	override delete<T>(url: string, options?: any): Observable<T> {
		const key = this.buildKey('DELETE', url, options);
		return this.enqueueWithKey<T>(
			key,
			() => super.delete(url, options) as unknown as Observable<T>,
			this.isNoDedup(options),
		) as any;
	}

	override patch<T>(url: string, body: any | null, options?: any): Observable<T> {
		const key = this.buildKey('PATCH', url, options, body);
		return this.enqueueWithKey<T>(
			key,
			() => super.patch(url, body, options) as unknown as Observable<T>,
			this.isNoDedup(options),
		) as any;
	}

	override head<T>(url: string, options?: any): Observable<T> {
		const key = this.buildKey('HEAD', url, options);
		return this.enqueueWithKey<T>(
			key,
			() => super.head(url, options) as unknown as Observable<T>,
			this.isNoDedup(options),
		) as any;
	}

	override options<T>(url: string, options?: any): Observable<T> {
		const key = this.buildKey('OPTIONS', url, options);
		return this.enqueueWithKey<T>(
			key,
			() => super.options(url, options) as unknown as Observable<T>,
			this.isNoDedup(options),
		) as any;
	}

	// перегрузка для request — для HttpRequest и string-формы
	override request(first: HttpRequest<unknown>): Observable<HttpEvent<unknown>>;
	override request<T>(first: string, url: string, options?: any): Observable<T>;
	override request<T>(first: string | HttpRequest<unknown>, url?: string, options?: any): Observable<any> {
		// HttpRequest — как есть - HttpEvent
		if (first instanceof HttpRequest) {
			const req = first as HttpRequest<unknown>;
			const key = this.buildKey(req.method || 'REQUEST', req.url, req, req.body);
			return this.enqueueWithKey<HttpEvent<unknown>>(
				key,
				() => super.request(req),
				this.isNoDedup(req),
			);
		}

		// string-форма
		const method = first;
		const reqUrl = url as string;
		const body = options?.body;
		const reqOptions = options;

		const key = this.buildKey(method, reqUrl, reqOptions, body);
		// вариант по умолчанию — наблюдаем тело. если реально передал observe: 'events', то понадобится использовать HttpRequest-вариант (перегрузка выше)
		return this.enqueueWithKey<T>(
			key,
			() => super.request(method as any, reqUrl as any, reqOptions) as unknown as Observable<T>,
			this.isNoDedup(reqOptions),
		);
	}
}
